import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Download, File as FileIcon, FileText, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// "onDelete" (optionnel) est un callback async(fileId) => boolean, fourni
// uniquement par les écrans où la suppression est autorisée (voir
// delete_event_attachments côté main courante) - FileViewer reste sinon un
// simple visualiseur, réutilisé ailleurs (consignes...) sans cette capacité.
const FileViewer = ({ fileIds = [], canDelete = false, onDelete }) => {
  const [files, setFiles] = useState([]); // [{ id, url, isImage, isPdf }]
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    let objectUrls = [];
    const load = async () => {
      const token = localStorage.getItem('token');
      const results = await Promise.all((fileIds || []).map(async (fileId) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/files/${fileId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) return { id: fileId, url: null, isImage: false, isPdf: false };
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          return { id: fileId, url, isImage: blob.type.startsWith('image/'), isPdf: blob.type === 'application/pdf' };
        } catch {
          return { id: fileId, url: null, isImage: false, isPdf: false };
        }
      }));
      setFiles(results);
    };
    if (fileIds && fileIds.length > 0) load();
    return () => { objectUrls.forEach(u => URL.revokeObjectURL(u)); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fileIds)]);

  if (!fileIds || fileIds.length === 0) return null;

  const handleDownload = (file) => {
    if (!file.url) return;
    const a = document.createElement('a');
    a.href = file.url;
    a.download = `fichier_${file.id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDelete = async (fileId) => {
    if (!onDelete) return;
    setDeletingId(fileId);
    try {
      await onDelete(fileId);
    } finally {
      setDeletingId(null);
    }
  };

  const images = files.filter(f => f.isImage && f.url);
  const pdfs = files.filter(f => f.isPdf && f.url);
  const others = files.filter(f => !f.isImage && !f.isPdf);

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-gray-700 dark:text-slate-200">Pièces jointes ({fileIds.length})</p>

      {/* Photos visibles directement, sans avoir besoin de les télécharger */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {images.map((file) => (
            <div key={file.id} className="relative">
              <button
                type="button"
                onClick={() => setLightboxUrl(file.url)}
                className="aspect-square w-full rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 hover:opacity-90 transition"
                data-testid={`image-preview-${file.id}`}
              >
                <img src={file.url} alt="Pièce jointe" className="w-full h-full object-cover" />
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleDelete(file.id); }}
                  disabled={deletingId === file.id}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center shadow hover:bg-red-700 disabled:opacity-50"
                  data-testid={`delete-file-${file.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* PDF consultables directement dans une popup, sans téléchargement */}
      {pdfs.map((file) => (
        <button
          key={file.id}
          type="button"
          onClick={() => setPreviewPdf(file.url)}
          className="w-full flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded border hover:bg-gray-100 dark:hover:bg-slate-700 transition"
          data-testid={`pdf-preview-${file.id}`}
        >
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            <span className="text-sm text-gray-700 dark:text-slate-200">Document PDF — cliquez pour prévisualiser</span>
          </div>
          <div className="flex items-center gap-2">
            <Download className="h-4 w-4 text-gray-400 dark:text-slate-500" onClick={(e) => { e.stopPropagation(); handleDownload(file); }} />
            {canDelete && (
              <Trash2
                className="h-4 w-4 text-red-500 hover:text-red-600"
                onClick={(e) => { e.stopPropagation(); if (deletingId !== file.id) handleDelete(file.id); }}
                data-testid={`delete-file-${file.id}`}
              />
            )}
          </div>
        </button>
      ))}

      {/* Fichiers non-image et non-PDF : ligne classique avec téléchargement */}
      {others.map((file) => (
        <div
          key={file.id}
          className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded border"
          data-testid={`file-${file.id}`}
        >
          <div className="flex items-center gap-2">
            <FileIcon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
            <span className="text-sm text-gray-700 dark:text-slate-200">Fichier joint</span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => handleDownload(file)}
              data-testid={`download-file-${file.id}`}
            >
              <Download className="h-4 w-4" />
            </Button>
            {canDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600"
                onClick={() => handleDelete(file.id)}
                disabled={deletingId === file.id}
                data-testid={`delete-file-${file.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}

      {/* Aperçu agrandi au clic */}
      <Dialog open={!!lightboxUrl} onOpenChange={(open) => !open && setLightboxUrl(null)}>
        <DialogContent className="max-w-3xl">
          {lightboxUrl && (
            <img src={lightboxUrl} alt="Aperçu" className="w-full h-auto rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Prévisualisation du PDF, sans téléchargement */}
      <Dialog open={!!previewPdf} onOpenChange={(open) => !open && setPreviewPdf(null)}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Prévisualisation du document</DialogTitle>
          </DialogHeader>
          {previewPdf && (
            <iframe title="Aperçu PDF" src={previewPdf} className="w-full h-full rounded-lg border border-slate-200 dark:border-slate-700" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileViewer;
